import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Ban, ShieldCheck, Save, Shield, Trash2 } from 'lucide-react';
import {
  fetchAdminUserDetail,
  adminToggleBan,
  adminToggleExempt,
  adminUpdateUserProfile,
  adminDeleteUser,
} from '../../hooks/useAdminUsers';
import type { AdminUserDetail as AdminUserDetailType } from '../../hooks/useAdminUsers';

interface Props {
  userId: string;
  onClose: () => void;
  onUpdated: () => void;
}

export const AdminUserDetail: React.FC<Props> = ({ userId, onClose, onUpdated }) => {
  const [detail, setDetail] = useState<AdminUserDetailType | null>(null);
  const [loading, setLoading] = useState(true);
  const [editingProfile, setEditingProfile] = useState(false);
  const [editForm, setEditForm] = useState({ full_name: '', email: '', avatar_url: '' });
  const [banModalOpen, setBanModalOpen] = useState(false);
  const [banReason, setBanReason] = useState('');
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteConfirmEmail, setDeleteConfirmEmail] = useState('');
  const [saving, setSaving] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetchAdminUserDetail(userId).then(({ data }) => {
      if (cancelled) return;
      if (data) {
        setDetail(data);
        setEditForm({
          full_name: data.profile.full_name || '',
          email: data.profile.email || '',
          avatar_url: data.profile.avatar_url || '',
        });
      }
      setLoading(false);
    });
    return () => { cancelled = true; };
  }, [userId]);

  const refreshDetail = async () => {
    const { data } = await fetchAdminUserDetail(userId);
    if (data) {
      setDetail(data);
      setEditForm({
        full_name: data.profile.full_name || '',
        email: data.profile.email || '',
        avatar_url: data.profile.avatar_url || '',
      });
    }
  };

  const handleSaveProfile = async () => {
    if (!detail) return;
    setSaving(true);
    setActionError(null);

    const fields: Record<string, string> = {};
    if (editForm.full_name !== (detail.profile.full_name || '')) fields.full_name = editForm.full_name;
    if (editForm.email !== detail.profile.email) fields.email = editForm.email;
    if (editForm.avatar_url !== (detail.profile.avatar_url || '')) fields.avatar_url = editForm.avatar_url;

    if (Object.keys(fields).length === 0) {
      setEditingProfile(false);
      setSaving(false);
      return;
    }

    const { error } = await adminUpdateUserProfile(userId, fields);
    setSaving(false);
    if (error) {
      setActionError(error);
      return;
    }
    setEditingProfile(false);
    await refreshDetail();
    onUpdated();
  };

  const handleToggleBan = async () => {
    if (!detail) return;
    const isBanning = !detail.profile.is_banned;
    if (isBanning && !banReason.trim()) return;
    setSaving(true);
    setActionError(null);
    const { error } = await adminToggleBan(userId, isBanning, banReason);
    setSaving(false);
    if (error) {
      setActionError(error);
      return;
    }
    setBanModalOpen(false);
    setBanReason('');
    onUpdated();
    onClose();
  };

  const handleToggleExempt = async () => {
    if (!detail) return;
    setSaving(true);
    setActionError(null);
    const newExempt = !detail.profile.is_exempt;
    const { error } = await adminToggleExempt(userId, newExempt);
    setSaving(false);
    if (error) {
      setActionError(error);
      return;
    }
    await refreshDetail();
    onUpdated();
  };

  const handleDeleteUser = async () => {
    if (!detail) return;
    if (deleteConfirmEmail !== detail.profile.email) return;
    setSaving(true);
    setActionError(null);
    const { error } = await adminDeleteUser(userId, deleteConfirmEmail);
    setSaving(false);
    if (error) {
      setActionError(error);
      return;
    }
    setDeleteModalOpen(false);
    onUpdated();
    onClose();
  };

  const panel = (
    <div className="fixed inset-0 z-50 flex justify-end" onClick={onClose}>
      <div className="absolute inset-0 bg-black/50" />
      <div
        className="relative w-full max-w-lg bg-neutral-950 border-l border-white/10 h-full overflow-y-auto p-6"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-lg font-semibold text-white">Detalhes do Usuário</h2>
          <button onClick={onClose} className="text-neutral-400 hover:text-white">
            <X className="w-5 h-5" />
          </button>
        </div>

        {actionError && (
          <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-3 mb-4 text-sm text-red-400">
            {actionError}
          </div>
        )}

        {loading ? (
          <div className="flex justify-center py-12">
            <div className="w-6 h-6 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : detail && (
          <div className="space-y-6">
            {/* Profile Section */}
            <section className="bg-neutral-900/50 rounded-xl p-4 space-y-3">
              <div className="flex items-center gap-3">
                <img
                  src={detail.profile.avatar_url || '/default-avatar.svg'}
                  alt=""
                  className="w-12 h-12 rounded-full bg-neutral-800"
                  onError={e => { (e.target as HTMLImageElement).src = '/default-avatar.svg'; }}
                />
                <div className="flex-1">
                  {editingProfile ? (
                    <div className="space-y-2">
                      <input
                        value={editForm.full_name}
                        onChange={e => setEditForm(f => ({ ...f, full_name: e.target.value }))}
                        placeholder="Nome completo"
                        className="w-full bg-neutral-800 border border-white/10 rounded px-2 py-1 text-base sm:text-sm text-white"
                      />
                      <input
                        value={editForm.email}
                        onChange={e => setEditForm(f => ({ ...f, email: e.target.value }))}
                        placeholder="Email"
                        className="w-full bg-neutral-800 border border-white/10 rounded px-2 py-1 text-base sm:text-sm text-white"
                      />
                      <input
                        value={editForm.avatar_url}
                        onChange={e => setEditForm(f => ({ ...f, avatar_url: e.target.value }))}
                        placeholder="URL do avatar"
                        className="w-full bg-neutral-800 border border-white/10 rounded px-2 py-1 text-base sm:text-sm text-white"
                      />
                      <div className="flex gap-2">
                        <button onClick={handleSaveProfile} disabled={saving} className="flex items-center gap-1 text-green-400 text-xs">
                          <Save className="w-3 h-3" /> {saving ? 'Salvando...' : 'Salvar'}
                        </button>
                        <button onClick={() => setEditingProfile(false)} className="text-neutral-500 text-xs">
                          Cancelar
                        </button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <p
                        className="text-white font-medium cursor-pointer hover:underline"
                        onClick={() => setEditingProfile(true)}
                      >
                        {detail.profile.full_name || '—'}{' '}
                        <span className="text-xs text-neutral-500">(clique para editar)</span>
                      </p>
                      <p className="text-sm text-neutral-400">{detail.profile.email}</p>
                    </>
                  )}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <span className="text-neutral-500">Role: </span>
                  <span className="text-white">{detail.profile.role}</span>
                </div>
                <div>
                  <span className="text-neutral-500">Forms: </span>
                  <span className="text-white">{detail.forms_count}</span>
                </div>
                <div>
                  <span className="text-neutral-500">Respostas: </span>
                  <span className="text-white">{detail.responses_count}</span>
                </div>
                <div>
                  <span className="text-neutral-500">Desde: </span>
                  <span className="text-white">
                    {new Date(detail.profile.created_at).toLocaleDateString('pt-BR')}
                  </span>
                </div>
              </div>
              {detail.profile.is_banned && (
                <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-3 text-sm">
                  <p className="text-red-400 font-medium">Usuário banido</p>
                  {detail.profile.banned_reason && (
                    <p className="text-red-300/70 text-xs mt-1">Motivo: {detail.profile.banned_reason}</p>
                  )}
                  {detail.profile.banned_at && (
                    <p className="text-red-300/70 text-xs">
                      Em: {new Date(detail.profile.banned_at).toLocaleDateString('pt-BR')}
                    </p>
                  )}
                </div>
              )}
            </section>

            {/* Subscription Section */}
            {detail.subscription && (
              <section className="bg-neutral-900/50 rounded-xl p-4">
                <h3 className="text-sm font-medium text-neutral-400 mb-2">Assinatura</h3>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div>
                    <span className="text-neutral-500">Status: </span>
                    <span className="text-white">{String(detail.subscription.status)}</span>
                  </div>
                  {detail.subscription.current_period_end && (
                    <div>
                      <span className="text-neutral-500">Período: </span>
                      <span className="text-white">
                        {new Date(String(detail.subscription.current_period_end)).toLocaleDateString('pt-BR')}
                      </span>
                    </div>
                  )}
                </div>
              </section>
            )}

            {/* Exempt Toggle */}
            <section>
              <button
                onClick={handleToggleExempt}
                disabled={saving}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium w-full justify-center ${
                  detail.profile.is_exempt
                    ? 'bg-purple-500/10 text-purple-400 border border-purple-500/20 hover:bg-purple-500/20'
                    : 'bg-neutral-800 text-neutral-400 border border-white/10 hover:bg-neutral-700'
                }`}
              >
                <Shield className="w-4 h-4" />
                {detail.profile.is_exempt ? 'Remover Isenção' : 'Isentar de Pagamento'}
              </button>
            </section>

            {/* Ban/Unban Action */}
            <section>
              <button
                onClick={() => setBanModalOpen(true)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium w-full justify-center ${
                  detail.profile.is_banned
                    ? 'bg-green-500/10 text-green-400 border border-green-500/20 hover:bg-green-500/20'
                    : 'bg-red-500/10 text-red-400 border border-red-500/20 hover:bg-red-500/20'
                }`}
              >
                {detail.profile.is_banned ? <ShieldCheck className="w-4 h-4" /> : <Ban className="w-4 h-4" />}
                {detail.profile.is_banned ? 'Desbanir Usuário' : 'Banir Usuário'}
              </button>
            </section>

            {/* Ban Confirmation Dialog */}
            {banModalOpen && (
              <div className="bg-neutral-900 border border-white/10 rounded-xl p-4 space-y-3">
                <p className="text-sm text-white">
                  {detail.profile.is_banned ? 'Confirmar desbanimento?' : 'Motivo do banimento (obrigatório):'}
                </p>
                {!detail.profile.is_banned && (
                  <textarea
                    value={banReason}
                    onChange={e => setBanReason(e.target.value)}
                    placeholder="Descreva o motivo..."
                    className="w-full bg-neutral-800 border border-white/10 rounded-lg p-2 text-sm text-white resize-none placeholder-neutral-500"
                    rows={3}
                  />
                )}
                <div className="flex gap-2">
                  <button
                    onClick={handleToggleBan}
                    disabled={(!detail.profile.is_banned && !banReason.trim()) || saving}
                    className="px-4 py-2 bg-red-500/20 text-red-400 rounded-lg text-sm disabled:opacity-50"
                  >
                    {saving ? 'Processando...' : 'Confirmar'}
                  </button>
                  <button
                    onClick={() => { setBanModalOpen(false); setBanReason(''); }}
                    className="px-4 py-2 bg-white/5 text-neutral-400 rounded-lg text-sm"
                  >
                    Cancelar
                  </button>
                </div>
              </div>
            )}

            {/* Audit History */}
            {detail.recent_audit.length > 0 && (
              <section>
                <h3 className="text-sm font-medium text-neutral-400 mb-2">Histórico de Ações Admin</h3>
                <div className="space-y-1">
                  {detail.recent_audit.map(entry => (
                    <div key={entry.id} className="text-xs text-neutral-500 flex justify-between py-1 border-b border-white/5 last:border-0">
                      <span className="text-neutral-300">{entry.action}</span>
                      <span>{new Date(entry.created_at).toLocaleDateString('pt-BR')}</span>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* Danger Zone — Delete User */}
            <section className="border-t border-red-500/20 pt-4">
              <h3 className="text-sm font-medium text-red-400 mb-2">Zona de Perigo</h3>
              <button
                onClick={() => setDeleteModalOpen(true)}
                className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium w-full justify-center bg-red-500/10 text-red-400 border border-red-500/20 hover:bg-red-500/20"
              >
                <Trash2 className="w-4 h-4" />
                Deletar Usuário
              </button>
            </section>

            {/* Delete Confirmation Dialog */}
            {deleteModalOpen && (
              <div className="bg-red-950/50 border border-red-500/20 rounded-xl p-4 space-y-3">
                <p className="text-sm text-red-300 font-medium">
                  Todos os formulários, respostas e webhooks deste usuário serão permanentemente excluídos. Esta ação não pode ser desfeita.
                </p>
                <p className="text-sm text-neutral-400">
                  Digite <strong className="text-white">{detail.profile.email}</strong> para confirmar:
                </p>
                <input
                  type="text"
                  value={deleteConfirmEmail}
                  onChange={e => setDeleteConfirmEmail(e.target.value)}
                  placeholder="Digite o email do usuário"
                  className="w-full bg-neutral-900 border border-red-500/20 rounded-lg px-3 py-2 text-sm text-white placeholder-neutral-500"
                />
                <div className="flex gap-2">
                  <button
                    onClick={handleDeleteUser}
                    disabled={deleteConfirmEmail !== detail.profile.email || saving}
                    className="px-4 py-2 bg-red-500 text-white rounded-lg text-sm font-medium disabled:opacity-50"
                  >
                    {saving ? 'Deletando...' : 'Deletar Permanentemente'}
                  </button>
                  <button
                    onClick={() => { setDeleteModalOpen(false); setDeleteConfirmEmail(''); }}
                    className="px-4 py-2 bg-white/5 text-neutral-400 rounded-lg text-sm"
                  >
                    Cancelar
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );

  return createPortal(panel, document.body);
};
